/**
 * Dispatch de eventos de conteúdo — fonte ÚNICA da regra "quais tipos de evento
 * disparam re-sync / config / orientação no player".
 *
 * <p>Reusado por:</p>
 * <ul>
 *   <li>{@code ContentSSEService.handleMessage} — canal Content SSE legado
 *       ({@code /api/sse/content}, {@code onmessage}).</li>
 *   <li>O listener do evento nomeado {@code content} do {@code RemoteOpsService}
 *       — canal SSE UNIFICADO ({@code /api/sse/device}, Epic D / Fase D2).</li>
 * </ul>
 *
 * <p>O envelope é IDÊNTICO nos dois canais: {@code {"type": "<tipo>", "data": {...}}}.
 * Manter a lista de tipos encaminhados aqui garante que um {@code content_changed}
 * (ou qualquer outro) dispara EXATAMENTE o mesmo comportamento, venha por onde vier.</p>
 */

import type { SSEEvent, SSEEventType } from './types';

/**
 * Tipos de evento que o player encaminha aos callbacks de mudança de conteúdo
 * (disparam re-sync, config, orientação ou trigger). Heartbeat/connected/command
 * NÃO estão aqui — são tratados à parte pelo transporte.
 *
 * <p>É EXATAMENTE o conjunto que o {@code ContentSSEService.handleMessage} sempre
 * encaminhou — não adicionar/remover sem alinhar os dois canais.</p>
 */
const FORWARDED_CONTENT_EVENT_TYPES: ReadonlySet<string> =
  new Set<SSEEventType>([
    'content_changed',
    'content_deleted',
    'library_updated',
    'programming_changed',
    // CP3.7 — Playlist editada (campanhas, mins, exclusividade) ou campanha
    // dentro de playlist alterada. Targeted (só players afetados).
    'playlist_changed',
    'config_update',
    'trigger_fired',
    'trigger_deactivated',
    'restart_command',
    'player_status_changed',
    // Admin trocou paisagem ↔ retrato no dropdown da Tabela de Players.
    'player_orientation_changed',
    // BATIDA DE SINCRONISMO (videowall). Não dispara re-sync: o `SyncService`
    // trata e retorna, só acertando o relógio (`applyServerTime`). Precisa estar
    // AQUI porque este é o único portão entre o canal unificado e o handler —
    // sem a entrada, o servidor publica a cada 15s e o player descarta calado.
    'sync_beat',
    // IMPULSO DE CORREÇÃO (supervisor de sincronismo). Também não dispara re-sync por si:
    // o `SyncService` acerta o relógio e avisa a store, que realinha a posição.
    'sync_correction',
  ]);

/**
 * `true` se o tipo do evento é encaminhado aos callbacks de conteúdo (dispara
 * re-sync/config/orientação/trigger). Tipos desconhecidos → `false` (ignorados,
 * mesmo comportamento do {@code handleMessage} legado).
 */
export function isForwardedContentEventType(type: string | undefined): boolean {
  return type !== undefined && FORWARDED_CONTENT_EVENT_TYPES.has(type);
}

/**
 * Parseia o envelope de conteúdo ({@code {"type","data"}}) do dado bruto de um
 * evento SSE. Retorna `null` quando o dado não é uma string JSON válida — o
 * caller trata (loga) sem quebrar o stream.
 */
export function parseContentEnvelope(raw: unknown): SSEEvent | null {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw) as SSEEvent;
  } catch {
    return null;
  }
}
